import * as React from 'react';
import {useState, useEffect} from 'react';
import {History} from 'history';
import {
    IPage,
    getAll as fetchPages,
    approve as approvePage,
    unapprove as unapprovePage,
    reject as rejectPage,
    submit as submitPage,
    deletePage,
} from '../../services/pages';
import {FormattedMessage, injectIntl, InjectedIntlProps, defineMessages} from 'react-intl';
import moment from 'moment';
import {Link, useParams} from 'react-router-dom';
import queryString from 'query-string';
import {PropagateLoader} from 'react-spinners';
import {IOrganization, IAccountDetails} from 'src/services/account';
import CreatePageModal from 'src/client/pages/CreatePageModal';
import {
    DASHBOARD_PATH,
    PAGE_UPDATE_PATH,
    PAGE_PATH,
    PARTNERS_PATH,
    SEARCH_PATH,
    PUBLIC_SEARCH_PATH,
    PAGES_PATH,
} from '../../constants';
import {isEmpty} from 'src/util';
import {get as getPartners} from '../../services/partner';
import {get as getDatabases} from '../../services/database';
import _ from 'lodash';

const messages = defineMessages({
    tableHdrTitle: {
        id: 'dash.pages.table.hdr.title',
        defaultMessage: 'Page Title',
    },
    tableHdrOrg: {
        id: 'dash.pages.table.hdr.org',
        defaultMessage: 'Organization',
    },
    tableHdrDb: {
        id: 'dash.pages.table.hdr.db',
        defaultMessage: 'Database',
    },
    tableHdrType: {
        id: 'dash.pages.table.hdr.type',
        defaultMessage: 'Type',
    },
    tableHdrDate: {
        id: 'dash.pages.table.hdr.date',
        defaultMessage: 'Updated',
    },
    tableHdrEmail: {
        id: 'dash.pages.table.hdr.email',
        defaultMessage: 'Email',
    },
    tableHdrApprove: {
        id: 'dash.pages.table.hdr.approve',
        defaultMessage: 'Approve',
    },
    tableHdrReject: {
        id: 'dash.pages.table.hdr.reject',
        defaultMessage: 'Reject',
    },
    tableHdrDelete: {
        id: 'dash.pages.table.hdr.delete',
        defaultMessage: 'Delete',
    },
    tableHdrRemove: {
        id: 'dash.pages.table.hdr.remove',
        defaultMessage: 'Remove',
    },
    tableHdrStatus: {
        id: 'dash.pages.table.hdr.status',
        defaultMessage: 'Status',
    },
    tableHdrEdit: {
        id: 'dash.pages.table.hdr.edit',
        defaultMessage: 'Edit',
    },
    filterBy: {
        id: 'dash.pages.filter.by',
        defaultMessage: 'Filter result by',
    },
    filters: {
        id: 'dash.pages.filters',
        defaultMessage: 'Filters',
    },
    filterClear: {
        id: 'dash.pages.filter.clear',
        defaultMessage: 'Clear filters',
    },
    confirmDelete: {
        id: 'dash.pages.del.confirm',
        defaultMessage: 'This page will be permanently deleted. Do you wish to proceed?',
    },
});

export default injectIntl(
    (
        props: InjectedIntlProps & {
            accountDetails: IAccountDetails;
            organization: IOrganization;
            history: History;
            updateAlertCounts: IUpdateAlertCounts;
        }
    ) => {
        const [myPages, setMyPages] = useState<IPage[]>([]);
        const [myPendingPages, setMyPendingPages] = useState<IPage[]>([]);
        const [partnerPages, setPartnerPages] = useState<IPage[]>([]);
        const [pendingPartnerPages, setPendingPartnerPages] = useState<IPage[]>([]);
        const [isLoadingPages, setIsLoadingPages] = useState<boolean>(false);
        const [canCreatePages, setCanCreatePages] = useState(true);
        const [currentTab, setCurrentTab] = useState<number>(0);

        const [tableSortObj, setTableSortObj] = useState({
            partners: {column: '', direction: ''},
            pendingPartners: {column: '', direction: ''},
            myPages: {column: '', direction: ''},
        });

        const getArrow = (table, column) => {
            return tableSortObj[table].column === column
                ? tableSortObj[table].direction === 'ASC'
                    ? '↓'
                    : '↑'
                : '';
        };
        const setSortObj = (table, column) => {
            const updatedObj = structuredClone(tableSortObj);
            updatedObj[table].column = column;
            updatedObj[table].direction = tableSortObj[table].direction === 'ASC' ? 'DESC' : 'ASC';
            setTableSortObj(updatedObj);
        };
        const sortData = (data, column, direction) =>
            data.sort((a: any, b: any) => {
                if (['created', 'updated'].includes(column)) {
                    return direction === 'ASC'
                        ? moment(_.get(a, column)).diff(moment(_.get(b, column)))
                        : moment(_.get(b, column)).diff(moment(_.get(a, column)));
                }

                return direction === 'ASC'
                    ? _.get(a, column).localeCompare(_.get(b, column))
                    : _.get(b, column).localeCompare(_.get(a, column));
            });

        const {tab} = queryString.parse(window.location.search);

        useEffect(() => {
            if (`${currentTab}` !== tab) props.history?.replace(`${PAGES_PATH}?tab=${currentTab}`);
        }, [currentTab]);

        useEffect(() => {
            if (tab && !Number.isNaN(parseInt(tab, 10))) setCurrentTab(parseInt(tab, 10));
        }, []);

        useEffect(() => {
            changeStatus(() => {});
            updateCanCreatePages();
        }, []);

        const updateCanCreatePages = async () => {
            const fetchedPartners = await getPartners();
            const fetchedDatabases = await getDatabases();
            if (fetchedPartners.success && fetchedDatabases.success) {
                setCanCreatePages(
                    !!(
                        Array.isArray(fetchedPartners.partners) && fetchedPartners.partners.length
                    ) ||
                        !!(
                            Array.isArray(fetchedDatabases.databases) &&
                            fetchedDatabases.databases.length
                        )
                );
            } else setCanCreatePages(false);
        };

        const loadPages = async () => {
            setIsLoadingPages(true);
            const requestResult = await fetchPages();
            setIsLoadingPages(false);
            const {success, pages: loadedPages} = requestResult;
            if (success && loadedPages) {
                setMyPages(loadedPages.filter((page) => isPage(page, true)));
                setPartnerPages(loadedPages.filter((page) => isPage(page, false) && page.approved));
                setPendingPartnerPages(
                    loadedPages.filter((page) => isPage(page, false) && page.status === 'reviewing')
                );
                props.updateAlertCounts({info: null});
            }
        };

        const changeStatus = async (callback: () => void) => {
            await callback();
            loadPages();
        };

        const confirmDelete = async (page: IPage) => {
            const r = confirm(props.intl.formatMessage(messages.confirmDelete));
            if (r) {
                await changeStatus(() => deletePage(page.name));
            }
        };

        const isPage = (page: IPage, mine: boolean) =>
            (mine && page.creatorName === props.accountDetails.name) ||
            (!mine && page.creatorName !== props.accountDetails.name);

        const handleNavigateToPage = async (page: IPage) => {
            const database = await getDatabases(page.databaseName);
            if (database?.database?.settings?.layout === 'brayn' || database?.database?.settings?.layout === 'customOne')
                window.open(
                    PUBLIC_SEARCH_PATH(database?.database?.name, page.accountName, page?.name),
                    '_blank'
                );
            else window.open(PAGE_PATH(page.name, page.accountName), '_blank');
        };

        const renderEmptySection = (mine: boolean) => {
            if (mine && !isLoadingPages && isEmpty(myPages) && isEmpty(myPendingPages)) {
                return (
                    <div className="p-5 d-flex justify-content-center">
                        <h3 className="text-muted">
                            <FormattedMessage
                                id="dash.info.pages.mine.empty.hdr"
                                defaultMessage="You do not have any pages yet."
                            />
                            <br />
                            <div className="d-flex justify-content-center p-4">
                                <CreatePageModal
                                    history={props.history}
                                    accountDetails={props.accountDetails}
                                    id="createPageModal1"
                                />
                            </div>
                        </h3>
                    </div>
                );
            }
            if (!mine && !isLoadingPages && isEmpty(partnerPages) && isEmpty(pendingPartnerPages)) {
                return (
                    <div className="p-5 d-flex justify-content-center">
                        <h3 className="text-muted">
                            <FormattedMessage
                                id="dash.info.pages.partners.empty.hdr"
                                defaultMessage="You do not have any partner pages."
                            />
                            <br />
                            <div className="d-flex justify-content-center">
                                <CreatePageModal
                                    history={props.history}
                                    accountDetails={props.accountDetails}
                                    id="createPageModal2"
                                />
                            </div>
                        </h3>
                    </div>
                );
            }
            return null;
        };

        const renderSubmitButton = (page) =>
            !page.approved &&
            page.accountName !== page.partnerName &&
            page.status !== 'reviewing' ? (
                <button
                    className="icon-btn accept"
                    onClick={() => changeStatus(() => submitPage(page.name))}>
                    Submit
                </button>
            ) : page.approved ? (
                <button
                    className="icon-btn"
                    onClick={() => changeStatus(() => unapprovePage(page.name))}>
                    Unpublish
                </button>
            ) : !page.approved && page.accountName === page.partnerName ? (
                <button
                    className="icon-btn accept"
                    onClick={() => changeStatus(() => approvePage(page.name))}>
                    Publish
                </button>
            ) : page.status === 'reviewing' && page.accountName !== page.partnerName ? (
                <button
                    className="icon-btn"
                    onClick={() => changeStatus(() => unapprovePage(page.name))}>
                    Withdraw
                </button>
            ) : null;

        const renderApprovedSection = () => {
            if (!partnerPages.length) return null;

            const table = 'partners';
            let sortedPartners = partnerPages.slice();

            if (tableSortObj[table].column) {
                sortedPartners = sortData(
                    sortedPartners,
                    tableSortObj[table].column,
                    tableSortObj[table].direction
                );
            }

            return (
                <React.Fragment>
                    <div className="row infoDetailsAra">
                        <div className="col-12 pb-3">
                            <FormattedMessage
                                id="dash.info.pages.all.hdr"
                                defaultMessage="All Partner Pages">
                                {(txt) => <h4 className="sub-title">{txt}</h4>}
                            </FormattedMessage>
                        </div>
                        <div className="col-12">
                            <p className="text-muted">
                                <FormattedMessage
                                    id="dash.info.pages.partner.all.txt"
                                    defaultMessage="Below is a list of all partner information pages in your database."
                                />
                            </p>
                        </div>
                    </div>

                    <div className="admintable">
                        <table>
                            <thead>
                                <tr className="trhead">
                                    <th onClick={(e) => setSortObj(table, 'title')}>
                                        {props.intl.formatMessage(messages.tableHdrTitle)}
                                        {getArrow(table, 'title')}
                                    </th>
                                    <th onClick={(e) => setSortObj(table, 'organization')}>
                                        {props.intl.formatMessage(messages.tableHdrOrg)}
                                        {getArrow(table, 'organization')}
                                    </th>
                                    <th onClick={(e) => setSortObj(table, 'databaseTitle')}>
                                        {props.intl.formatMessage(messages.tableHdrDb)}
                                        {getArrow(table, 'databaseTitle')}
                                    </th>
                                    <th onClick={(e) => setSortObj(table, 'updated')}>
                                        {props.intl.formatMessage(messages.tableHdrDate)}
                                        {getArrow(table, 'updated')}
                                    </th>
                                    <th />
                                    <th className="text-center">
                                        {props.intl.formatMessage(messages.tableHdrDelete)}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedPartners.map((page, index) => {
                                    return (
                                        <tr key={index} className="trcontent">
                                            <td>
                                                <button
                                                    className="btn btn-link a"
                                                    onClick={() => handleNavigateToPage(page)}>
                                                    {page.title}
                                                </button>
                                            </td>
                                            <td>{page.organization}</td>
                                            <td>{page.databaseTitle}</td>
                                            <td>
                                                {page.updated == null
                                                    ? moment(page.created).format('LLL')
                                                    : moment(page.updated).format('LLL')}
                                            </td>
                                            <td>
                                                {page.status !== 'draft' && (
                                                    <button
                                                        className="icon-btn"
                                                        onClick={() =>
                                                            changeStatus(() =>
                                                                unapprovePage(page.name)
                                                            )
                                                        }>
                                                        Unpublish
                                                    </button>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <button
                                                    className="icon-btn"
                                                    onClick={() => confirmDelete(page)}>
                                                    <i className="fas fa-trash-alt" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </React.Fragment>
            );
        };

        const renderPendingSection = () => {
            // console.warn('setPartnerPages', partnerPages)
            if (!pendingPartnerPages.length) return null;

            const table = 'pendingPartners';
            let sortedData = pendingPartnerPages.slice();

            if (tableSortObj[table].column) {
                sortedData = sortData(
                    sortedData,
                    tableSortObj[table].column,
                    tableSortObj[table].direction
                );
            }

            return (
                <React.Fragment>
                    {/* TODO span with count not rendered */}
                    <FormattedMessage
                        id="dash.info.tab.pages.pending"
                        defaultMessage="New Information Pages {count}"
                        values={{count: <span>{pendingPartnerPages.length}</span>}}>
                        {(txt) => <h4 className="sub-title mt-2">{txt}</h4>}
                    </FormattedMessage>
                    <FormattedMessage
                        tagName="p"
                        id="dash.info.tab.partner.pages.txt"
                        defaultMessage="You have new or recently modified information pages waiting for review."
                    />

                    <div className="admintable">
                        <table>
                            <thead>
                                <tr className="trhead">
                                    <th onClick={(e) => setSortObj(table, 'title')}>
                                        {props.intl.formatMessage(messages.tableHdrTitle)}
                                        {getArrow(table, 'title')}
                                    </th>

                                    <th onClick={(e) => setSortObj(table, 'organization')}>
                                        {props.intl.formatMessage(messages.tableHdrOrg)}
                                        {getArrow(table, 'organization')}
                                    </th>

                                    <th onClick={(e) => setSortObj(table, 'databaseTitle')}>
                                        {props.intl.formatMessage(messages.tableHdrDb)}
                                        {getArrow(table, 'databaseTitle')}
                                    </th>

                                    <th>{props.intl.formatMessage(messages.tableHdrType)}</th>

                                    <th onClick={(e) => setSortObj(table, 'updated')}>
                                        {props.intl.formatMessage(messages.tableHdrDate)}
                                        {getArrow(table, 'updated')}
                                    </th>
                                    <th className="text-center">
                                        {props.intl.formatMessage(messages.tableHdrApprove)}
                                    </th>
                                    <th className="text-center">
                                        {props.intl.formatMessage(messages.tableHdrReject)}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedData.map((page, index) => {
                                    return (
                                        <tr key={index} className="trcontent">
                                            <td>
                                                <button
                                                    className="btn btn-link a"
                                                    onClick={() => handleNavigateToPage(page)}>
                                                    {page.title}
                                                </button>
                                            </td>
                                            <td>{page.organization}</td>
                                            <td>{page.databaseTitle}</td>
                                            <td>{page.type}</td>
                                            <td>
                                                {page.updated == null
                                                    ? moment(page.created).format('LLL')
                                                    : moment(page.updated).format('LLL')}
                                            </td>
                                            <td className="text-center">
                                                <button
                                                    className="icon-btn accept"
                                                    onClick={() =>
                                                        changeStatus(() => approvePage(page.name))
                                                    }>
                                                    <i className="fas fa-check" />
                                                </button>
                                            </td>
                                            <td className="text-center">
                                                <button
                                                    className="icon-btn reject"
                                                    onClick={() =>
                                                        changeStatus(() => rejectPage(page.name))
                                                    }>
                                                    <i className="fas fa-times" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="row">
                        <div className="col-lg-12 martop-30">
                            <hr />
                        </div>
                    </div>
                </React.Fragment>
            );
        };

        const renderMyPagesAsPartner = () => {
            if (myPages.length > 0) {
                const table = 'myPages';
                let sortedData = myPages.slice();

                if (tableSortObj[table].column) {
                    sortedData = sortData(
                        sortedData,
                        tableSortObj[table].column,
                        tableSortObj[table].direction
                    );
                }
                return (
                    <React.Fragment>
                        <h4 className="sub-title" style={{display: 'inline'}}>
                            My Pages
                        </h4>
                        <CreatePageModal
                            history={props.history}
                            accountDetails={props.accountDetails}
                        />
                        <p className="text-muted pt-3">
                            <FormattedMessage
                                tagName="p"
                                id="dash.info.pages.my.all.txt"
                                defaultMessage="Below is a list of all your information pages in partner databases."
                            />
                        </p>
                        <div className="admintable m-0">
                            <table>
                                <thead>
                                    <tr className="trhead">
                                        <th onClick={(e) => setSortObj(table, 'title')}>
                                            {props.intl.formatMessage(messages.tableHdrTitle)}
                                            {getArrow(table, 'title')}
                                        </th>
                                        <th onClick={(e) => setSortObj(table, 'databaseTitle')}>
                                            {props.intl.formatMessage(messages.tableHdrDb)}
                                            {getArrow(table, 'databaseTitle')}
                                        </th>
                                        <th onClick={(e) => setSortObj(table, 'updated')}>
                                            {props.intl.formatMessage(messages.tableHdrDate)}
                                            {getArrow(table, 'updated')}
                                        </th>
                                        <td>{props.intl.formatMessage(messages.tableHdrStatus)}</td>
                                        <td>Action</td>
                                        <td>Edit</td>
                                        <td>Delete</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedData.map((page, index) => {
                                        return (
                                            <tr key={index} className="trcontent">
                                                <td>
                                                    <button
                                                        className="btn btn-link a"
                                                        onClick={() => handleNavigateToPage(page)}>
                                                        {page.title}
                                                    </button>
                                                </td>
                                                <td>{page.databaseTitle}</td>
                                                <td>
                                                    {page.updated == null
                                                        ? moment(page.created).format('LLL')
                                                        : moment(page.updated).format('LLL')}
                                                </td>
                                                <td
                                                    className={
                                                        page.status === 'rejected'
                                                            ? 'text-danger'
                                                            : page.approved
                                                            ? 'text-success'
                                                            : ''
                                                    }>
                                                    {page.status === 'rejected'
                                                        ? 'Rejected'
                                                        : page.status === 'draft'
                                                        ? 'Draft'
                                                        : page.status === 'reviewing'
                                                        ? 'In Review'
                                                        : page.status === 'reviewed' &&
                                                          page.approved
                                                        ? 'Approved'
                                                        : ''}
                                                </td>
                                                <td>{renderSubmitButton(page)}</td>
                                                <td className="text-center">
                                                    <Link
                                                        className="btn icon-btn"
                                                        to={`${PAGE_UPDATE_PATH(page.name)}`}>
                                                        <i className="fas fa-edit" />
                                                    </Link>
                                                </td>
                                                <td className="text-center">
                                                    <button
                                                        className="icon-btn"
                                                        onClick={() => confirmDelete(page)}>
                                                        <i className="fas fa-trash-alt" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </React.Fragment>
                );
            } else {
                return renderEmptySection(true);
            }
        };

        const renderMyPagesAsClient = () => {
            // console.warn('renderMyPagesAsClient', myPages)
            if (isEmpty(myPages)) return null;
            else {
                const table = 'myPages';
                let sortedData = myPages.slice();

                if (tableSortObj[table].column) {
                    sortedData = sortData(
                        sortedData,
                        tableSortObj[table].column,
                        tableSortObj[table].direction
                    );
                }

                return (
                    <div className="row infoDetailsAra">
                        <div className="col-12 pb-3">
                            <FormattedMessage
                                id="dash.pages.tab.mine.hdr"
                                defaultMessage="{orgName}'s Pages"
                                values={{orgName: props.organization.orgName}}>
                                {(txt) => (
                                    <h4 className="sub-title" style={{display: 'inline'}}>
                                        {txt}
                                    </h4>
                                )}
                            </FormattedMessage>
                            <CreatePageModal
                                history={props.history}
                                accountDetails={props.accountDetails}
                            />
                        </div>
                        <div className="col-12">
                            <p className="text-muted font-weight-light">
                                <FormattedMessage
                                    id="dash.info.pages.mine.all.txt"
                                    values={{orgName: props.organization.orgName}}
                                    defaultMessage="Below is a list of all information pages that belong to {orgName}."
                                />
                            </p>
                        </div>
                        <div className="col-12">
                            <div className="admintable">
                                <table>
                                    <thead>
                                        <tr className="trhead">
                                            <th onClick={(e) => setSortObj(table, 'title')}>
                                                {props.intl.formatMessage(messages.tableHdrTitle)}
                                                {getArrow(table, 'title')}
                                            </th>
                                            <th onClick={(e) => setSortObj(table, 'databaseTitle')}>
                                                {props.intl.formatMessage(messages.tableHdrDb)}
                                                {getArrow(table, 'databaseTitle')}
                                            </th>
                                            <th onClick={(e) => setSortObj(table, 'updated')}>
                                                {props.intl.formatMessage(messages.tableHdrDate)}
                                                {getArrow(table, 'updated')}
                                            </th>
                                            {/* <td>{props.intl.formatMessage(messages.tableHdrEmail)}</td> */}
                                            <th className="text-center">
                                                {props.intl.formatMessage(messages.tableHdrStatus)}
                                            </th>
                                            <th className="text-center">Action</th>
                                            <th className="text-center">Edit</th>
                                            <th className="text-center">Delete</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedData.map((page, index) => {
                                            return (
                                                <tr key={index} className="trcontent">
                                                    <td>
                                                        <button
                                                            className="btn btn-link a"
                                                            onClick={() =>
                                                                handleNavigateToPage(page)
                                                            }>
                                                            {page.title}
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <Link
                                                            to={SEARCH_PATH(
                                                                page.databaseName,
                                                                page.accountName
                                                            )}>
                                                            {page.databaseTitle}
                                                        </Link>
                                                    </td>
                                                    <td>
                                                        {page.updated == null
                                                            ? moment(page.created).format('LLL')
                                                            : moment(page.updated).format('LLL')}
                                                    </td>
                                                    <td className="text-center">
                                                        {page.approved
                                                            ? 'Published'
                                                            : page.status === 'draft'
                                                            ? 'Draft'
                                                            : 'Pending Approval'}
                                                    </td>
                                                    <td>{renderSubmitButton(page)}</td>
                                                    <td className="text-center">
                                                        {page.approved ? (
                                                            <Link
                                                                className="btn icon-btn"
                                                                to={`${PAGE_UPDATE_PATH(
                                                                    page.name
                                                                )}`}>
                                                                <i className="fas fa-edit" />
                                                            </Link>
                                                        ) : page.accountName ===
                                                          page.partnerName ? (
                                                            <Link
                                                                className="btn icon-btn"
                                                                to={`${PAGE_UPDATE_PATH(
                                                                    page.name
                                                                )}`}>
                                                                <i className="fas fa-edit" />
                                                            </Link>
                                                        ) : page.status === 'draft' ? (
                                                            <Link
                                                                className="btn icon-btn"
                                                                to={`${PAGE_UPDATE_PATH(
                                                                    page.name
                                                                )}`}>
                                                                <i className="fas fa-edit" />
                                                            </Link>
                                                        ) : null}
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            className="icon-btn"
                                                            onClick={() => confirmDelete(page)}>
                                                            <i className="fas fa-trash-alt" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );
            }
        };

        const renderBreadcrumb = () => {
            return (
                <div className="col-lg-12 breadcrumb-col">
                    <ul>
                        <li>
                            <Link to={`${DASHBOARD_PATH}`}>
                                <FormattedMessage
                                    id="dash.info.bc.dashboard"
                                    defaultMessage="Dashboard"
                                />
                            </Link>
                        </li>
                        <li>
                            {/* TODO pseudo elems */}
                            <i className="fas fa-angle-right" />
                        </li>
                        <li>
                            <FormattedMessage
                                id="dash.info.bc.pages"
                                defaultMessage="Information Pages"
                            />
                        </li>
                    </ul>
                </div>
            );
        };

        const renderPageTabNav = () => {
            if (props.accountDetails.subscriptionLevel > 0) {
                return (
                    <ul className="nav navtabs" id="myTab" role="tablist">
                        <li className="nav-item">
                            <a
                                className={`nav-link ${!currentTab && 'active'}`}
                                onClick={() => setCurrentTab(0)}>
                                <FormattedMessage
                                    id="dash.info.nav.pages.partner"
                                    defaultMessage="Partner Pages Tab"
                                />
                            </a>
                        </li>
                        <li className="nav-item">
                            <a
                                className={`nav-link ${currentTab && 'active'}`}
                                onClick={() => setCurrentTab(1)}>
                                {props.organization.orgName || 'My Org'}'s Pages
                            </a>
                        </li>
                    </ul>
                );
            } else {
                return null;
            }
        };

        if (!canCreatePages && !myPages.length && !myPendingPages.length && !partnerPages.length) {
            return (
                <div className="col-12 my-5">
                    <Link to={PARTNERS_PATH}>
                        <i
                            className="fas fa-user-plus text-light-gray p-4 ml-auto d-block text-center"
                            style={{fontSize: 100}}
                        />
                    </Link>
                    <div className="d-flex justify-content-center">
                        <small className="text-muted">
                            You need to have partners or a database to create info pages. Invite
                            partners to collaborate and help build a database!
                        </small>
                    </div>
                </div>
            );
        }

        return (
            <div
                className="tab-pane show active mar-top-35 mobo-padding"
                id="InfoPages"
                role="tabpanel"
                aria-labelledby="contact-tab">
                <div className="container-fluid">
                    <div className="row">
                        {renderBreadcrumb()}
                        <div className="col-lg-12 pagetitle">
                            <h2>
                                <FormattedMessage
                                    id="dash.info.pages.hdr"
                                    defaultMessage="Information Pages"
                                />
                            </h2>
                        </div>

                        <div className="col-lg-12 infotab">
                            {renderPageTabNav()}
                            <div className="tab-content" id="partnerPages">
                                {isLoadingPages ? (
                                    <div className="p-5 d-flex justify-content-center">
                                        <PropagateLoader
                                            size={15}
                                            loading={isLoadingPages}
                                            color="#29447b"
                                        />
                                    </div>
                                ) : props.accountDetails.subscriptionLevel > 0 ? (
                                    <div className="tab-pane fade show active">
                                        {currentTab === 0 ? (
                                            <div>
                                                {renderPendingSection()}
                                                {renderApprovedSection()}
                                                {renderEmptySection(false)}
                                            </div>
                                        ) : (
                                            <div>
                                                {renderMyPagesAsClient()}
                                                {renderEmptySection(true)}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div>
                                        <div>{renderMyPagesAsPartner()}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);



// WEBPACK FOOTER //
// ./src/client/dashboard/Pages.tsx